import React, { useEffect, useState, useMemo } from "react";
import { Popup } from "devextreme-react/popup";
import { SelectBox } from "devextreme-react/select-box";
import { TextBox } from "devextreme-react/text-box";
import { Button } from "devextreme-react/button";
import ScrollView from "devextreme-react/scroll-view";
import * as DinamicQueries from "../../api/DinamicsQuery";

const AsignarTipoServicioClientePopup = ({ visible, onClose, establishmentIds = [], onSaveSuccess }) => {
  const [clientesEstablecimientos, setClientesEstablecimientos] = useState([]);
  const [tiposPorCliente, setTiposPorCliente] = useState({});
  const [storeTypes, setStoreTypes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingStoreTypes, setLoadingStoreTypes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const clientesFiltrados = useMemo(() => {
    if (!busqueda || !busqueda.trim()) return clientesEstablecimientos;
    const term = String(busqueda).trim().toLowerCase();
    return clientesEstablecimientos.filter((c) => (c.nombre || "").toLowerCase().includes(term));
  }, [clientesEstablecimientos, busqueda]);

  useEffect(() => {
    if (!visible) return;
    setLoadingStoreTypes(true);
    DinamicQueries.getDataWithParameters("callProcedure", "analytics/", {
      method: "sp_get_store_type",
      params: {},
    })
      .then((response) => {
        const data = response?.data ?? [];
        setStoreTypes(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error al cargar tipos de establecimiento:", err);
        setStoreTypes([]);
      })
      .finally(() => setLoadingStoreTypes(false));
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const hasIds = Array.isArray(establishmentIds) ? establishmentIds.length > 0 : establishmentIds != null && establishmentIds !== "";
    if (!hasIds) {
      setClientesEstablecimientos([]);
      setTiposPorCliente({});
      return;
    }
    setLoadingClientes(true);
    const idStoreParam = Array.isArray(establishmentIds) ? establishmentIds.join(",") : String(establishmentIds);
    DinamicQueries.getDataWithParameters("callProcedure", "analytics/", {
      method: "sp_type_store_for_client",
      params: { id_store: idStoreParam },
    })
      .then((response) => {
        const data = response?.data ?? [];
        const seen = new Set();
        const unique = [];
        (data || []).forEach((item) => {
          const id = item?.ID_CLIENTE ?? item?.id_cliente;
          const nombre = item?.CLIENTE ?? item?.cliente ?? "";
          const idType = item?.ID_TYPE ?? item?.id_type ?? null;
          if (id != null && !seen.has(id)) {
            seen.add(id);
            unique.push({ id, nombre, id_type: idType != null ? Number(idType) : null });
          }
        });
        setClientesEstablecimientos(unique);
        const initial = unique.reduce((acc, c) => ({ ...acc, [c.id]: c.id_type ?? null }), {});
        setTiposPorCliente(initial);
      })
      .catch((err) => {
        console.error("Error al cargar clientes de los establecimientos:", err);
        setClientesEstablecimientos([]);
        setTiposPorCliente({});
      })
      .finally(() => setLoadingClientes(false));
  }, [visible, establishmentIds]);

  const handleTipoChange = (clienteId, value) => {
    setTiposPorCliente((prev) => ({ ...prev, [clienteId]: value }));
  };

  const handleGuardar = () => {
    const ids = Array.isArray(establishmentIds) ? establishmentIds : [establishmentIds];
    if (!ids.length) return;
    // Mismo listado de (cliente, tipo) para todos los establecimientos: el SP crea un registro por (store, client).
    const items = clientesEstablecimientos.map((c) => ({
      id_client: c.id,
      id_type: tiposPorCliente[c.id] ?? null,
    }));
    setSaving(true);
    // Una llamada por establecimiento: cada una inserta en store_type_clients un registro por cliente para ese store.
    const promises = ids.map((idStore) =>
      DinamicQueries.getDataWithParameters("saveStoreTypeClients", "analytics/", {
        id_store: Number(idStore),
        items,
      })
    );
    Promise.all(promises)
      .then((responses) => {
        const allOk = responses.every((r) => r?.data?.msg !== false);
        if (allOk) {
          onSaveSuccess?.();
          onClose();
        }
      })
      .catch((err) => {
        console.error("Error al guardar tipos por cliente:", err);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      showTitle={true}
      title="Asignar tipo de servicio por cliente"
      showCloseButton={true}
      width="90%"
      maxWidth={900}
      height="85vh"
    >
      <div className="p-3 d-flex flex-column" style={{ height: "100%", minHeight: 0 }}>
        <p className="text-muted small mb-3">
          Clientes de los {Array.isArray(establishmentIds) ? establishmentIds.length : 1} establecimiento(s) seleccionado(s). Asigne un tipo de servicio a cada uno.
        </p>
        <div className="mb-3">
          <TextBox
            value={busqueda}
            onValueChanged={(e) => setBusqueda(e.value ?? "")}
            placeholder="Buscar cliente..."
            mode="search"
            showClearButton={true}
            stylingMode="outlined"
            width="100%"
          />
        </div>
        {loadingClientes && <p className="text-muted small">Cargando clientes...</p>}
        <ScrollView
          width="100%"
          height="100%"
          className="flex-grow-1"
          showScrollbar="always"
          scrollByContent={false}
          style={{ minHeight: 200, flex: "1 1 0" }}
        >
          <div className="d-flex flex-column gap-3 pr-2">
            {clientesFiltrados.map((cliente) => (
              <div
                key={cliente.id}
                className="d-flex align-items-center"
                style={{ minHeight: 40, width: "100%" }}
              >
                <span
                  className="fw-medium"
                  style={{
                    flex: "0 0 35%",
                    maxWidth: "35%",
                    textAlign: "left",
                    paddingRight: 8,
                  }}
                >
                  {cliente.nombre}
                </span>
                <div
                  style={{
                    flex: "0 0 65%",
                    maxWidth: "65%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <SelectBox
                    dataSource={storeTypes}
                    displayExpr="NAME"
                    valueExpr="ID"
                    value={tiposPorCliente[cliente.id] ?? cliente.id_type ?? null}
                    onValueChanged={(e) => handleTipoChange(cliente.id, e.value)}
                    placeholder={loadingStoreTypes ? "Cargando tipos..." : "Seleccione tipo"}
                    label="Tipo"
                    labelMode="static"
                    style={{ width: "100%", maxWidth: 320 }}
                    searchEnabled
                    showClearButton
                    disabled={loadingStoreTypes}
                  />
                </div>
              </div>
            ))}
            {!loadingClientes && clientesEstablecimientos.length === 0 && (
              <p className="text-muted small">No hay clientes para los establecimientos seleccionados.</p>
            )}
            {!loadingClientes && clientesEstablecimientos.length > 0 && clientesFiltrados.length === 0 && (
              <p className="text-muted small">Ningún cliente coincide con la búsqueda.</p>
            )}
          </div>
        </ScrollView>
        <div className="d-flex justify-content-end gap-2 mt-3 pt-3 flex-shrink-0" style={{ borderTop: "1px solid #eee" }}>
          <Button text="Cancelar" onClick={onClose} disabled={saving} />
          <Button text="Guardar" type="default" onClick={handleGuardar} disabled={saving} />
        </div>
      </div>
    </Popup>
  );
};

export default AsignarTipoServicioClientePopup;

